import { useMemo, type CSSProperties, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Badge, Button, Donut, PriorityPill, type Priority } from '@shared/design-system';
import { type RecentTicket, type ExpiringLicense } from '@/services/dashboardService';
import { useDashboardStats, useAdminDashboardStats } from '@/hooks/queries/useDashboard';
import { usePageHeaderActions, useAuth } from '@/contexts';
import { Skeleton } from '@/components/ui/skeleton';
import { ActivityFeed } from '@/components/ActivityFeed';

// ─── Local primitives (design-screens lineage) ────────────────────

interface CardProps {
  title?: ReactNode;
  hint?: ReactNode;
  action?: ReactNode;
  children?: ReactNode;
  padding?: number;
  style?: CSSProperties;
}

function Card({ title, hint, action, children, padding = 18, style }: CardProps) {
  return (
    <div
      style={{
        background: 'white',
        border: '1px solid var(--dg-border)',
        borderRadius: 'var(--dg-r-lg)',
        boxShadow: 'var(--dg-shadow-sm)',
        overflow: 'hidden',
        ...style,
      }}
    >
      {(title || action) && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: `14px ${padding}px 12px`,
            borderBottom: '1px solid var(--dg-border)',
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            {title && (
              <div
                style={{
                  fontSize: 13.5,
                  fontWeight: 600,
                  color: 'var(--dg-text-1)',
                  letterSpacing: '-0.005em',
                }}
              >
                {title}
              </div>
            )}
            {hint && (
              <div style={{ fontSize: 12, color: 'var(--dg-text-3)', marginTop: 1 }}>{hint}</div>
            )}
          </div>
          {action}
        </div>
      )}
      <div style={{ padding }}>{children}</div>
    </div>
  );
}

interface KpiCardProps {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  badges?: { label: string; tone: 'danger' | 'warn' | 'ok' | 'neutral' }[];
  href?: string;
}

function KpiCard({ label, value, sub, badges, href }: KpiCardProps) {
  const inner = (
    <div
      style={{
        background: 'white',
        border: '1px solid var(--dg-border)',
        borderRadius: 'var(--dg-r-lg)',
        padding: 16,
        boxShadow: 'var(--dg-shadow-sm)',
        height: '100%',
        transition: 'border-color 120ms, box-shadow 120ms',
      }}
    >
      <div
        style={{
          fontSize: 12,
          fontWeight: 500,
          color: 'var(--dg-text-3)',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 26,
          fontWeight: 600,
          color: 'var(--dg-text-1)',
          letterSpacing: '-0.025em',
          fontVariantNumeric: 'tabular-nums',
          marginTop: 6,
        }}
      >
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: 12, color: 'var(--dg-text-3)', marginTop: 4 }}>{sub}</div>
      )}
      {badges && badges.length > 0 && (
        <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
          {badges.map((b, i) => (
            <Badge key={i} tone={b.tone} size="sm">{b.label}</Badge>
          ))}
        </div>
      )}
    </div>
  );
  return href ? <Link to={href} style={{ textDecoration: 'none' }}>{inner}</Link> : inner;
}

// ─── Helpers ──────────────────────────────────────────────────────

const tryFormat = (n: number) =>
  new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(n);

function priorityToPill(priority: string): Priority {
  if (priority === 'Kritik' || priority === 'Acil') return 'Acil';
  if (priority === 'Yüksek') return 'Yüksek';
  if (priority === 'Düşük') return 'Düşük';
  return 'Normal';
}

// ─── Page ─────────────────────────────────────────────────────────

export function Dashboard() {
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  // The admin stats endpoint is gated server-side by [Authorize(Roles = "Admin")];
  // only fetch for Admins so non-Admins don't trigger a spurious 403 error toast.
  const isAdmin = user?.roles?.includes('Admin') ?? false;
  // ActivityFeedController is gated by settings.view (audit-log data is tenant-wide
  // and cross-user). ActivityFeed uses a bare fetch + local error state, so a 403
  // renders an inline "Aktiviteler yuklenemedi" box instead of a toast — hide the
  // whole card for roles without the permission (e.g. Manager) rather than showing
  // an error where the honest answer is "not available to you".
  const canViewActivity = hasPermission('settings.view');
  const statsQuery = useDashboardStats();
  const adminStatsQuery = useAdminDashboardStats(isAdmin);

  usePageHeaderActions(
    useMemo(
      () => (
        <Button variant="primary" size="sm" icon="plus" onClick={() => navigate('/tickets')}>
          Yeni Talep
        </Button>
      ),
      [navigate],
    ),
  );

  const stats = statsQuery.data ?? null;
  const adminStats = adminStatsQuery.data ?? null;
  const loading = statsQuery.isPending;

  if (loading && !stats) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-[14px]">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28" />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-[14px]">
          <Skeleton className="h-80" />
          <Skeleton className="h-80" />
        </div>
      </div>
    );
  }

  const ticketStats = stats?.ticketStats;
  const openTickets = (ticketStats?.open || 0) + (ticketStats?.inProgress || 0);
  const slaStats = stats?.slaStats;
  const priorityDist = stats?.priorityDistribution;
  const totalForPriority = ticketStats?.total ?? 0;
  const recentTickets = stats?.recentTickets ?? [];
  const expiringLicenses = stats?.licenseAlerts?.expiringLicenses ?? [];
  const expiringCount = stats?.licenseAlerts?.expiringCount ?? 0;
  const expiredCount = stats?.licenseAlerts?.expiredCount ?? 0;
  const showLicenseAlerts = expiringCount > 0 || expiredCount > 0;

  const donutSegments = [
    { value: ticketStats?.open || 0,       color: '#3730a3', label: 'Açık' },
    { value: ticketStats?.inProgress || 0, color: '#d97706', label: 'İşlemde' },
    { value: ticketStats?.waiting || 0,    color: '#64748b', label: 'Beklemede' },
    { value: ticketStats?.resolved || 0,   color: '#10b981', label: 'Çözüldü' },
  ].filter((s) => s.value > 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* KPI strip — base */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-[14px]">
        <KpiCard
          label="Açık Talepler"
          value={openTickets}
          sub={`${ticketStats?.total || 0} toplam`}
          href="/tickets"
          badges={[
            ...(ticketStats?.critical ? [{ label: `${ticketStats.critical} kritik`, tone: 'danger' as const }] : []),
            ...(ticketStats?.high     ? [{ label: `${ticketStats.high} yüksek`,    tone: 'warn'   as const }] : []),
          ]}
        />
        <KpiCard
          label="Müşteriler"
          value={stats?.totalCustomers || 0}
          sub={`${stats?.activeCustomers || 0} aktif`}
          href="/customers"
        />
        <KpiCard
          label="Kullanıcılar"
          value={stats?.totalUsers || 0}
          sub={`${stats?.activeUsers || 0} aktif`}
          href="/users"
        />
        <KpiCard
          label="Ekipler"
          value={stats?.totalTeams || 0}
          sub={`${stats?.totalRoles || 0} rol tanımlı`}
          href="/teams"
        />
      </div>

      {/* KPI strip — admin (conditional) */}
      {adminStats?.revenueStats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-[14px]">
          <KpiCard
            label="MRR"
            value={tryFormat(adminStats.revenueStats.monthlyRecurringRevenue)}
            sub="Tekrarlayan aylık gelir"
          />
          <KpiCard
            label="Aktif Sözleşmeler"
            value={adminStats.revenueStats.activeContracts}
            sub={
              adminStats.revenueStats.expiringContractsThisMonth > 0
                ? `${adminStats.revenueStats.expiringContractsThisMonth} bu ay bitiyor`
                : tryFormat(adminStats.revenueStats.activeContractsValue)
            }
            href="/contracts"
          />
          <KpiCard
            label="Bu Ay Çözülen"
            value={adminStats.ticketStats.resolvedThisMonth}
            sub={`Bu hafta ${adminStats.ticketStats.resolvedThisWeek}`}
          />
          <KpiCard
            label="Ortalama Çözüm"
            value={
              adminStats.ticketStats.averageResolutionHours > 0
                ? `${Math.round(adminStats.ticketStats.averageResolutionHours)} sa`
                : '—'
            }
            sub={
              adminStats.ticketStats.overdueTickets > 0
                ? `${adminStats.ticketStats.overdueTickets} geciken`
                : 'SLA içinde'
            }
          />
        </div>
      )}

      {/* Status donut + SLA */}
      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-[14px]">
        <Card
          title="Talep Durumu"
          hint={`${ticketStats?.total || 0} toplam talep`}
          action={
            <Link to="/tickets" style={{ fontSize: 12, color: 'var(--dg-brand)', fontWeight: 500, textDecoration: 'none' }}>
              Tümü →
            </Link>
          }
        >
          {donutSegments.length > 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 24, padding: '4px 0' }}>
              <Donut
                size={150}
                thickness={22}
                segments={donutSegments}
                label={ticketStats?.total ?? 0}
                sub="Toplam"
              />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 9, fontSize: 13, flex: 1 }}>
                {[
                  { c: '#3730a3', l: 'Açık',      v: ticketStats?.open || 0 },
                  { c: '#d97706', l: 'İşlemde',   v: ticketStats?.inProgress || 0 },
                  { c: '#64748b', l: 'Beklemede', v: ticketStats?.waiting || 0 },
                  { c: '#10b981', l: 'Çözüldü',   v: ticketStats?.resolved || 0 },
                  { c: '#94a3b8', l: 'Kapandı',   v: ticketStats?.closed || 0 },
                ].map((r) => (
                  <div key={r.l} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 8, height: 8, borderRadius: 999, background: r.c, flexShrink: 0 }} />
                    <span style={{ flex: 1, color: 'var(--dg-text-2)' }}>{r.l}</span>
                    <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: 'var(--dg-text-1)' }}>
                      {r.v}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--dg-text-3)', fontSize: 13 }}>
              Henüz talep yok
            </div>
          )}
        </Card>

        <Card title="SLA Uyumu" hint="Aktif SLA'lı talepler">
          {(slaStats?.totalWithSla ?? 0) > 0 ? (
            <>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 14 }}>
                <div
                  style={{
                    fontSize: 32,
                    fontWeight: 600,
                    letterSpacing: '-0.025em',
                    color:
                      (slaStats?.complianceRate ?? 0) >= 90
                        ? '#10b981'
                        : (slaStats?.complianceRate ?? 0) >= 70
                          ? '#d97706'
                          : '#dc2626',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {slaStats?.complianceRate ?? 0}%
                </div>
                <Badge tone={(slaStats?.complianceRate ?? 0) >= 90 ? 'ok' : 'warn'} size="sm">
                  {slaStats?.totalWithSla} SLA'lı
                </Badge>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {[
                  { l: 'Zamanında', v: slaStats?.onTime ?? 0,    c: '#10b981' },
                  { l: 'Risk',      v: slaStats?.atRisk ?? 0,    c: '#d97706' },
                  { l: 'Aşım',      v: slaStats?.breached ?? 0,  c: '#dc2626' },
                ].map((r) => {
                  const pct = (slaStats?.totalWithSla ?? 0) > 0
                    ? (r.v / slaStats!.totalWithSla) * 100
                    : 0;
                  return (
                    <div key={r.l}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, marginBottom: 4 }}>
                        <span style={{ color: 'var(--dg-text-2)' }}>{r.l}</span>
                        <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: 'var(--dg-text-1)' }}>
                          {r.v}
                        </span>
                      </div>
                      <div
                        style={{
                          height: 6,
                          background: 'var(--dg-surface-2)',
                          borderRadius: 999,
                          overflow: 'hidden',
                        }}
                      >
                        <div style={{ width: `${pct}%`, height: '100%', background: r.c, borderRadius: 999 }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--dg-text-3)', fontSize: 13 }}>
              Henüz SLA tanımlı talep yok
            </div>
          )}
        </Card>
      </div>

      {/* Recent tickets + Priority dist */}
      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-[14px]">
        <Card
          title="Son Talepler"
          hint={`${recentTickets.length} kayıt`}
          action={
            <Link to="/tickets" style={{ fontSize: 12, color: 'var(--dg-brand)', fontWeight: 500, textDecoration: 'none' }}>
              Tümü →
            </Link>
          }
          padding={0}
        >
          {recentTickets.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {recentTickets.map((t: RecentTicket, i: number) => (
                <Link
                  key={t.id}
                  to={`/tickets/${t.id}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '10px 18px',
                    borderBottom: i < recentTickets.length - 1 ? '1px solid var(--dg-border)' : 'none',
                    textDecoration: 'none',
                    color: 'inherit',
                  }}
                >
                  <span style={{ fontSize: 11, fontFamily: 'var(--dg-mono)', color: 'var(--dg-text-3)', width: 78 }}>
                    {t.ticketNumber}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: 'var(--dg-text-1)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {t.title}
                    </div>
                    {(t.customerName || t.assigneeName) && (
                      <div style={{ fontSize: 11.5, color: 'var(--dg-text-3)' }}>
                        {[t.customerName, t.assigneeName].filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </div>
                  <PriorityPill level={priorityToPill(t.priority)} />
                  <Badge tone="neutral" size="sm">{t.status}</Badge>
                </Link>
              ))}
            </div>
          ) : (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--dg-text-3)', fontSize: 13 }}>
              Henüz talep yok
            </div>
          )}
        </Card>

        <Card title="Öncelik Dağılımı" hint="Açık talepler">
          {totalForPriority > 0 && priorityDist ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[
                { l: 'Kritik', v: priorityDist.critical, c: '#dc2626' },
                { l: 'Yüksek', v: priorityDist.high,     c: '#d97706' },
                { l: 'Orta',   v: priorityDist.medium,   c: '#3730a3' },
                { l: 'Düşük',  v: priorityDist.low,      c: '#64748b' },
              ].map((r) => {
                const pct = totalForPriority > 0 ? (r.v / totalForPriority) * 100 : 0;
                return (
                  <div key={r.l}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, marginBottom: 4 }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--dg-text-2)' }}>
                        <span style={{ width: 8, height: 8, borderRadius: 999, background: r.c }} />
                        {r.l}
                      </span>
                      <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: 'var(--dg-text-1)' }}>
                        {r.v}
                      </span>
                    </div>
                    <div style={{ height: 6, background: 'var(--dg-surface-2)', borderRadius: 999, overflow: 'hidden' }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: r.c, borderRadius: 999 }} />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--dg-text-3)', fontSize: 13 }}>
              Veri yok
            </div>
          )}
        </Card>
      </div>

      {/* Activity feed (full width) — settings.view gated, see comment above */}
      {canViewActivity && (
        <Card title="Son Aktivite" padding={0}>
          <div style={{ padding: 4 }}>
            <ActivityFeed limit={10} maxHeight="380px" />
          </div>
        </Card>
      )}

      {/* License alerts (conditional) */}
      {showLicenseAlerts && (
        <Card
          title="Lisans Uyarıları"
          hint={`${expiringCount} bitmek üzere · ${expiredCount} süresi dolmuş`}
          action={
            <Link to="/licenses" style={{ fontSize: 12, color: 'var(--dg-brand)', fontWeight: 500, textDecoration: 'none' }}>
              Tüm lisanslar →
            </Link>
          }
          padding={0}
        >
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {expiringLicenses.map((lic: ExpiringLicense, i: number) => (
              <Link
                key={lic.id}
                to="/licenses"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 18px',
                  borderBottom: i < expiringLicenses.length - 1 ? '1px solid var(--dg-border)' : 'none',
                  textDecoration: 'none',
                  color: 'inherit',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--dg-text-1)' }}>{lic.name}</div>
                  {lic.productName && (
                    <div style={{ fontSize: 11.5, color: 'var(--dg-text-3)' }}>{lic.productName}</div>
                  )}
                </div>
                <Badge tone={lic.daysUntilExpiration <= 7 ? 'danger' : 'warn'} size="sm">
                  {lic.daysUntilExpiration} gün
                </Badge>
              </Link>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
